import React, { useEffect, useRef, useState } from 'react';
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess, UserHasAccess } from 'components/user-access';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FormDateRangeInput, FormTextInput, FormLocationSelector } from 'components/form-fields';
import { SetFiltering } from 'state/actions';
import { ListingTable } from 'components/listing/ListingTable';
import { FleetService, CheckService, TyreCheckService, LinkService } from 'services';
import moment from 'moment';
import { Button, TextButton } from 'components/buttons';
import { StringToEllipsesString } from 'components/formatting/StringToEllipsesString';
import { Intent, Position, Icon } from '@blueprintjs/core';
import { NotificationToaster, NotificationInline } from 'components/notifications';
import { ShowHide } from 'components/layout';
import { Filter, FilterButton } from 'components/filtering';
import { useDebounce } from 'hooks/useDebounce';
import { AssetStatus } from 'components/status/AssetStatus';
import { InternalLink } from 'components/navigation';
import { Tooltip, } from 'components/tooltip';
import { DefectSignoffModal } from "components/Defect";
import { AssetHelperFunctions } from 'helpers';
import { EventViewModal } from './modals/EventViewModal';
import { EventEditModal } from 'pages/asset/events/modals/EventEditModal';
import { AssetTypeFilterRenderer } from "pages/vehicle/asset-type-filter-renderer/AssetTypeFilterRenderer";
import { ReportList } from "components/reporting/ReportList";
import classNames from 'classnames';

export function DefectListing({
    asset,
    eventOptions,
    mappedAssetOptions,
    loading,
    pageState,
    locationList,
    eventTypes
}) {
    const eventAction = "FMS:View";
    const eventEditAction = "FMS:Edit";
    const checksAction = "Checks:View";
    const checksEditAction = "Checks:SignOff";
    const tireAction = "FleetVIS:View";
    const tireEditAction = "FleetVIS:Edit"
    const canAccessFleet = UserHasAccess(eventAction);
    const canAccessChecks = UserHasAccess(checksAction);
    const canAccessTire = UserHasAccess(tireAction);
    const canSignoffEventDefect = UserHasAccess(eventEditAction);
    const canSignoffCheckDefect = UserHasAccess(checksEditAction);
    const canSignoffTireDefect = UserHasAccess(tireEditAction);

    const canEditPage = canSignoffEventDefect || canSignoffCheckDefect || canSignoffTireDefect;

    const debounceTimeoutMs = 750;
    const truncateLength = 15;
    const truncateLengthSmall = 10;
    const defaultWeekStart = -2;
    const defaultTimeoutMs = 500;

    const [loadingData, setLoadingData] = useState(true);
    const [savingSignoff, setSavingSignoff] = useState(false);
    const [showDefectSignoffModal, setShowDefectSignoffModal] = useState(false);
    const [isEventViewModalOpen, setIsEventViewModalOpen] = useState(false);
    const [isEventEditModalOpen, setIsEventEditModalOpen] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [searchTerm, setSearchTerm] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [expandedAssetTypes, setExpandedAssetTypes] = useState({});
    const [reportDownloading, setReportDownloading] = useState(false);

    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeoutMs);
    const dispatch = useDispatch();
    const inputSearchRef = useRef(null);
    const eventHubReduxName = "eventHubRedux" + pageState; //This is the shared redux for the hub
    const defectListingFilterReduxName = "defectListingFilterRedux" + pageState; //This is the redux for the filters
    const defectListingReduxName = "defectListingRedux" + pageState; //This is the page redux
    const dateFormat = "YYYY-MM-DD";
    const dateDisplayFormat = "DD/MM/YYYY";
    const actionHeader = ["Actions"];

    const tableHeadersHub = [
        "ID/Registration",
        "",
        "Fleet Number",
        "Location"
    ];

    const tableHeadersProfileInital = [
        "Type",
        "Name",
        "Defect Date",
        "Defect Comment",
        "Status",
        "Maintenance Provider",
        "Rectified",
        "Signoff"
    ];

    const sortableHeaders = [
        { headerName: "ID/Registration", sortName: "AssetRegistration" },
        { headerName: "Fleet Number", sortName: "FleetNumber" },
        { headerName: "Location", sortName: "AssetLocation" },
        { headerName: "Type", sortName: "DefectType" },
        { headerName: "Name", sortName: "DefectName" },
        { headerName: "Defect Date", sortName: "DefectDate" },
        { headerName: "Status", sortName: "DefectStatus" },
        { headerName: "Maintenance Provider", sortName: "MaintenanceProvider" },
        { headerName: "Rectified", sortName: "RectifiedDate" },
        { headerName: "Signoff", sortName: "SignoffDate" }
    ];

    // Table state
    const defaultRecordSize = 25;
    const defaultSortName = pageState === "HUB" ? "ID/Registration" : "Defect Date";
    const tableHeadersProfile = canEditPage ? [...tableHeadersProfileInital, ...actionHeader] : tableHeadersProfileInital;
    const hubFilterCount = 5;
    const profileFilterCount = 3;
    const filterColumnCount = pageState === "HUB" ? hubFilterCount : profileFilterCount;
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultRecordSize);
    const [tableSortBy, setTableSortBy] = useState(sortableHeaders.find(sh => sh.headerName === defaultSortName));
    const [tableSortDir, setTableSortDir] = useState("A");
    const [startDate, setStartDate] = useState(moment().add(defaultWeekStart, 'w').format(dateFormat));
    const [endDate, setEndDate] = useState(moment().add(2, 'w').format(dateFormat));
    const [pageFilters, setPageFilters] = useState([]);
    const [reduxLoaded, setReduxLoaded] = useState(false);
    const [eventListingFilterReduxLoaded, setEventListingFilterReduxLoaded] = useState(false);
    const [eventHubReduxLoaded, setEventHubReduxLoaded] = useState(false);
    const [eventListingReduxLoaded, setEventListingReduxLoaded] = useState(false);
    const [focusToSearchTerm, setFocusToSearchTerm] = useState(false);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [locationDefaultsToTrue, setLocationDefaultsToTrue] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [localDefect, setLocalDefect] = useState(null);
    const [predefinedFilters, setPredefinedFilters] = useState([]);
    const [predefinedFiltersSetupComplete, setPredefinedFiltersSetupComplete] = useState(false);

    const currentPageSize = useRef();
    const currentPageNumber = useRef();
    const currentSortBy = useRef();
    const currentSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentStartDate = useRef();
    const currentEndDate = useRef();
    const currentSearchTerm = useRef();
    const currentSelectedLocations = useRef();

    const noDataMessage = "No results found, please try altering the search criteria.";
    const location = useLocation();

    const eventHubRedux = useSelector(state => state.filters[eventHubReduxName]);
    const eventListingRedux = useSelector(state => state.filters[defectListingReduxName]);
    const eventListingFilterRedux = useSelector(state => state.filters[defectListingFilterReduxName]) || [];

    if (eventListingFilterRedux.length === 0 && pageState === "HUB") {
        dispatch(SetFiltering(defectListingFilterReduxName, AssetHelperFunctions.defaultAssetStatusFilters));
    }

    const assetTypeOptions = mappedAssetOptions?.assetTypes || [];
    const assetStatusOptions = mappedAssetOptions?.assetStatusesMappings || [];
    const assetOwnershipOptions = mappedAssetOptions?.ownershipAttributeMappings || [];

    useEffect(() => {
        if (eventListingFilterReduxLoaded && eventHubReduxLoaded && eventListingReduxLoaded) {
            setReduxLoaded(true);
        }
    }, [eventListingFilterReduxLoaded, eventHubReduxLoaded, eventListingReduxLoaded]);

    useEffect(() => {
        if (!eventListingFilterReduxLoaded) {
            setAppliedFilters(eventListingFilterRedux ?? []);
            setEventListingFilterReduxLoaded(true);
        }
    }, [eventListingFilterRedux]);

    useEffect(() => {
        if (!eventListingReduxLoaded) {
            setEventListingReduxLoaded(true);
            if (eventListingRedux) {
                setPageNumber(eventListingRedux.pageNumber);
                setPageSize(eventListingRedux.pageSize);
                setTableSortDir(eventListingRedux.tableSortDir);
                setTableSortBy(eventListingRedux.tableSortBy);
                setSearchTerm(eventListingRedux.searchTerm);
            } else {
                setSearchTerm("");
            }
        }
    }, [eventListingRedux]);

    useEffect(() => {
        if (!eventHubReduxLoaded) {
            setEventHubReduxLoaded(true);
            if (eventHubRedux) {
                setStartDate(eventHubRedux.startDate);
                setEndDate(eventHubRedux.endDate);
            }
            if (pageState === 'HUB') {
                if (eventHubRedux) {
                    setSelectedLocations(eventHubRedux.locations);
                } else {
                    setLocationDefaultsToTrue(true);
                }
            }
        }
    }, [eventHubRedux]);

    useEffect(() => {
        if (reduxLoaded && mappedAssetOptions) {
            let defectTypeFilter = {
                name: "DEFECTTYPE",
                displayName: "Defect Type",
                items: [
                ]
            };
            if (canAccessFleet) {
                defectTypeFilter.items.push({ id: "Event", name: "Event" });
            }
            if (canAccessChecks) {
                defectTypeFilter.items.push({id: "Check", name: "Check" });
            }
            if (canAccessTire) {
                defectTypeFilter.items.push({ id: "Tyre Check", name: "Tyre Check" });
            }
            let allFilters = [
                {
                    name: "ASSETSTATUSID",
                    displayName: "Asset Status",
                    items: assetStatusOptions
                },
                {
                    name: "ASSETOWNERSHIPID",
                    displayName: "Asset Ownership",
                    items: assetOwnershipOptions
                },
                {
                    name: "ASSETTYPE",
                    displayName: "Asset Type",
                    items: assetTypeOptions
                },
                {
                    name: "DEFECTSTATUS",
                    displayName: "Defect Status",
                    items: [
                        { id: "Open", name: "Open" },
                        { id: "Rectified", name: "Rectified" },
                        { id: "Not Rectified", name: "Not Rectified" },
                        { id: "Monitored", name: "Monitored" }
                    ]
                }
            ];
            if (defectTypeFilter.items.length > 1) {
                allFilters.push(defectTypeFilter);
            }
            if (pageState === "HUB") {
                setPageFilters(allFilters);
            } else {
                setPageFilters(allFilters.filter(f => !["ASSETSTATUSID", "ASSETOWNERSHIPID", "ASSETTYPE"].includes(f.name)));
            }
        }
    }, [location.search, pageState, dispatch, reduxLoaded, mappedAssetOptions]);

    useEffect(() => {
        if (tableRows) {
            setFocusToSearchTerm(true);
        }
    }, [tableRows]);

    useEffect(() => {
        if (!loading && reduxLoaded && selectedLocations && predefinedFiltersSetupComplete) {
            fetchTableData();
        }
    }, [loading, pageSize, pageNumber, tableSortBy, tableSortDir, appliedFilters, startDate, endDate, debouncedSearchTerm, reduxLoaded, selectedLocations, predefinedFiltersSetupComplete]);

    useEffect(() => {
        if (focusToSearchTerm && !loading) {
            setTimeout(function () {
                if (inputSearchRef.current != null) {
                    inputSearchRef.current.focus();
                }
            }, defaultTimeoutMs);
            setFocusToSearchTerm(false);
        }
    }, [focusToSearchTerm, loading]);

    useEffect(() => {
        if (!localDefect?.assetId) {
            return;
        }
        setShowDefectSignoffModal(true);
    }, [localDefect]);

    useEffect(() => {
        if (pageState !== 'HUB' && asset?.assetLocation?.location?.id) {
            setPredefinedFilters([
                { key: "AssetId", value: `${asset.assetId}` },
                { key: "Location", value: `${asset?.assetLocation?.location?.id}` },
            ]);
            setSelectedAsset(asset);
        }
    }, [asset, pageState]);

    useEffect(() => {
        if (predefinedFilters && predefinedFilters.length > 0) {
            setPredefinedFiltersSetupComplete(true);
        }
    }, [predefinedFilters]);

    useEffect(() => {
        if (pageState === 'HUB') {
            setPredefinedFiltersSetupComplete(true);
        }
    }, []);

    function onClickViewEventDefect(defect, rowAsset) {
        let event = {
            eventId: defect.parent?.id,
            eventTypeId: defect.parent?.typeId
        }
        setSelectedAsset(rowAsset);
        setSelectedEvent(event);
        setIsEventViewModalOpen(true);
    }

    function onEventViewModalClose() {
        setSelectedEvent(null);
        setSelectedAsset(null);
        setIsEventViewModalOpen(false);
    }

    const onSignoffItem = (defect) => {
        setLocalDefect(defect);
    };

    function onCloseSignoffModal() {
        setShowDefectSignoffModal(false);
        setLocalDefect(null);
    }

    function hasSignoffPermission(defect) {
        switch (defect?.defectType) {
            case "Check":
                return canSignoffCheckDefect;
            case "Tyre Check":
                return canSignoffTireDefect;
            default:
                return canSignoffEventDefect;
        }
    }

    function signOffDefect(defectSignOff) {
        setSavingSignoff(true);

        switch (defectSignOff?.defectType) {
            case "Check":
                CheckService.signOffDefect(localDefect.parent.id, localDefect.defectId, defectSignOff).then(
                    () => {
                        NotificationToaster.show(Intent.SUCCESS, "Defect signed off successfully.");
                        fetchTableData(true);
                        onCloseSignoffModal();
                    })
                    .catch((error) => {
                        NotificationToaster.show(Intent.DANGER, error);
                    })
                    .finally(() => {
                        setSavingSignoff(false);
                    });
                break;
            case "Tyre Check":
                TyreCheckService.signOffDefect(localDefect.defectId, localDefect.parent.id, defectSignOff).then(
                    () => {
                        FleetService.signOffSyncTireDefect(localDefect.defectId, defectSignOff).then(
                            () => {
                                NotificationToaster.show(Intent.SUCCESS, "Defect signed off successfully.");
                                fetchTableData(true);
                                onCloseSignoffModal();
                            })
                            .catch((error) => {
                                NotificationToaster.show(Intent.DANGER, error);
                            })
                            .finally(() => {
                                setSavingSignoff(false);
                            });
                    })
                    .catch((error) => {
                        NotificationToaster.show(Intent.DANGER, error);
                        setSavingSignoff(false);
                    });
                break;
            default:
                //This is for Event
                FleetService.signOffDefect(localDefect.parent.id, localDefect.defectId, defectSignOff).then(
                    () => {
                        NotificationToaster.show(Intent.SUCCESS, "Defect signed off successfully.");
                        fetchTableData(true);
                        onCloseSignoffModal();
                    })
                    .catch((error) => {
                        NotificationToaster.show(Intent.DANGER, error);
                    })
                    .finally(() => {
                        setSavingSignoff(false);
                    });
        }
    }

    function getDefectNameLink(defect, index) {
        if (defect?.defectType === "Check") {
            return <span className="nowrap"><InternalLink link={`/checks/${defect.parent?.id}/details?area=fleet`} key={`view-defect-${defect.assetId}-${index}`}>
                <StringToEllipsesString length={truncateLength} showTooltip={true}>{defect.defectName}</StringToEllipsesString></InternalLink>
            </span>
        } else if (defect?.defectType === "Tyre Check") {
            return <a href={`${window.env.VISION_URL}/#/Fleet/RedirectToProfile?assetId=${defect.assetId}&tab=inspectionsTab`} key={`view-defect-${defect.assetId}-${index}`}>
                <StringToEllipsesString length={truncateLength} showTooltip={true}>{defect.defectName}</StringToEllipsesString>
            </a>
        } else {
            let rowAsset = ({
                registration: defect.assetRegistration,
                assetLocation: {
                    assetStatus: {
                        id: defect.assetStatus?.id
                    },
                    assetOwnership: {
                        id: defect.assetOwnership?.id
                    }
                }
            });
            return <span className="nowrap"><TextButton onClick={() => onClickViewEventDefect(defect, rowAsset)} className="text-align-left" key={`view-defect-${defect.assetId}-${index}`}>
                <StringToEllipsesString length={truncateLength} showTooltip={true}>{defect.defectName}</StringToEllipsesString>
            </TextButton></span>
        }
    }

    function mapDefectsToTable(defects) {
        const rows = defects.map((defect, index) => {
            let defectStatusClass = "";
            switch (defect?.defectStatus) {
                case "Rectified":
                    defectStatusClass = "text-intent-success";
                    break;
                case "NotRectified":
                    defectStatusClass = "text-intent-danger";
                    break;
                case "Monitored":
                    defectStatusClass = "text-intent-warning";
                    break;
                default:
                    //This is Open
                    defectStatusClass = "text-intent-primary";
                    break;
            }

            let row = [];
            if (pageState === 'HUB') {
                row.push(
                    <div className="nowrap inline-items" key={`${defect.assetId}-${index}`}>
                        <AssetStatus statusId={defect.assetStatus.id} ownershipId={defect.assetOwnership.id} isLoading={loading} statusAttributeMappings={mappedAssetOptions.assetStatusesMappings} ownershipAttributeMappings={mappedAssetOptions.ownershipAttributeMappings} tooltipPosition={Position.RIGHT} />
                        <Tooltip position={Position.TOP} content={`Asset Profile`}>
                            <InternalLink link={LinkService.getVehicleProfileUrl(defect.assetId, 'defects', "area=MMFH")}>{defect.assetRegistration}</InternalLink>
                        </Tooltip>
                    </div>,
                    <div className="nowrap inline-items" key={`asset-icon-${defect.assetId}-${index}`}>
                        {
                            AssetHelperFunctions.getVehicleIcon(
                                defect.assetType?.id,
                                defect.assetType?.name,
                                defect.assetSubtype?.name)
                        }
                    </div>,
                    defect.fleetNumber,
                    defect.assetLocation.name
                );
            }
            row.push(
                defect.defectType,
                getDefectNameLink(defect, index),
                defect.defectDate ? moment(defect.defectDate).format(dateDisplayFormat) : "",
                <StringToEllipsesString length={truncateLength} showTooltip={true}>{defect.defectComment}</StringToEllipsesString>,
                <span className={defectStatusClass}>{defect.defectStatus === "NotRectified" ? "Not rectified" : defect.defectStatus}</span>,
                <StringToEllipsesString length={truncateLength} showTooltip={true}>{defect.maintenanceProvider}</StringToEllipsesString>,
                <StringToEllipsesString length={truncateLengthSmall} showTooltip={true}>{defect.rectifiedDate ? `${moment(defect.rectifiedDate).format(dateDisplayFormat)} - ${defect.rectifiedBy}` : ""}</StringToEllipsesString>,
                <StringToEllipsesString length={truncateLengthSmall} showTooltip={true}>{defect.signoffDate ? `${moment(defect.signoffDate).format(dateDisplayFormat)} - ${defect.signoffUser}` : ""}</StringToEllipsesString>
            );

            if (!hasSignoffPermission(defect)) {
                if (canEditPage) {
                    row.push(<></>);
                }
                return row;
            }

            if (!defect.signoffDate) {
                row.push(
                    <div key={`defect-signoff-${defect.assetId}-${index}`}>
                        <Tooltip key={`signoff-tt-${defect.assetId}-${index}`} content={`Signoff`}>
                            <Button iconOnly icon="tick-circle" minimal large={false} onClick={() => onSignoffItem(defect)} />
                        </Tooltip>
                    </div>
                );
            } else if (defect.rectified) {
                let signoffDetails = `Signed off by ${defect.signoffUser} on ${moment(defect.signoffDate).format("DD MMM YYYY")}. ${defect.rectifiedComment}.`;
                row.push(
                    <div key={`defect-signoff-${defect.assetId}-${index}`} style={{ paddingLeft: '7px' }}>
                        <Tooltip key={`signoff-tt-${defect.assetId}-${index}`} content={signoffDetails}>
                            <Icon icon="tick-circle" intent={Intent.SUCCESS} />
                        </Tooltip>
                    </div>
                );
            } else if (defect.monitored) {
                let signoffDetails = `Set to monitored by ${defect.signoffUser} on ${moment(defect.signoffDate).format("DD MMM YYYY")}. ${defect.rectifiedComment}.`;
                row.push(
                    <div key={`defect-signoff-${defect.assetId}-${index}`}>
                        <Tooltip key={`signoff-tt-${defect.assetId}-${index}`} content={signoffDetails}>
                            <Button iconOnly icon="search" intent={Intent.WARNING} minimal large={false} onClick={() => onSignoffItem(defect)} />
                        </Tooltip>
                    </div>
                );
            } else {
                let signoffDetails = `Set to not rectified by ${defect.signoffUser} on ${moment(defect.signoffDate).format("DD MMM YYYY")}. ${defect.rectifiedComment}.`;
                row.push(
                    <div key={`defect-signoff-${defect.assetId}-${index}`}>
                        <Tooltip key={`signoff-tt-${defect.assetId}-${index}`} content={signoffDetails}>
                            <Button iconOnly icon="delete" intent={Intent.DANGER} minimal large={false} onClick={() => onSignoffItem(defect)} />
                        </Tooltip>
                    </div>
                );
            }

            return row;
        });
        setTableRows(rows);
    }

    function fetchTableData(forceReload) {
        if ((!reduxLoaded ||
            //Need to do this check as we need to use the debouncedSearchTerm, but we need to only load once if the seachTerm is set in the redux so once it's set we know we have the redux all loaded
            searchTerm === null ||
            (searchTerm?.length > 0 && debouncedSearchTerm === null) ||
            !appliedFilters) ||
            !forceReload &&
            currentPageSize.current === pageSize &&
            currentPageNumber.current === pageNumber &&
            currentSortBy.current === tableSortBy &&
            currentSortDir.current === tableSortDir &&
            currentAppliedFilters.current === appliedFilters.length &&
            currentStartDate.current === startDate &&
            currentEndDate.current === endDate &&
            currentSelectedLocations.current === selectedLocations &&
            (currentSearchTerm.current ?? "") === (debouncedSearchTerm ?? "")
        ) {
            return;
        }

        if (pageState !== 'HUB' && !asset?.assetLocation?.location?.id) {
            //The profile page hasn't loaded yet
            return;
        }

        if (pageNumber > 1 && (currentSearchTerm.current ?? "") !== (debouncedSearchTerm ?? "")) {
            //Can't set the page number when this changes else the debounce doesn't work. The page number change should kick this off again.
            setPageNumber(1);
            return;
        }

        setLoadingData(true);

        currentPageSize.current = pageSize;
        currentPageNumber.current = pageNumber;
        currentSortBy.current = tableSortBy;
        currentSortDir.current = tableSortDir;
        currentAppliedFilters.current = appliedFilters.length;
        currentStartDate.current = startDate;
        currentEndDate.current = endDate;
        currentSelectedLocations.current = selectedLocations;
        currentSearchTerm.current = (debouncedSearchTerm ?? "");

        const allFilters = [];

        if (appliedFilters && appliedFilters.length > 0) {
            allFilters.push(...appliedFilters.map(f => {
                if (f.name === "AssetTypeSubtype" && typeof f.value === "string" && f.value.startsWith("uncat-")) {
                    return { key: "AssetType", value: f.parentAssetTypeId.toString() };
                }
                return { key: f.name, value: String(f.value) };
            }));
        }

        if (pageState === 'HUB' && selectedLocations && selectedLocations.length > 0) {
            allFilters.push(...selectedLocations.map(sl => ({ key: "Location", value: `${sl}` })));
        } else {
            allFilters.push(...predefinedFilters);
        }

        FleetService.searchAssetDefects(
            startDate,
            endDate,
            pageSize,
            pageNumber,
            debouncedSearchTerm === null ? searchTerm : debouncedSearchTerm.trim(),
            tableSortBy.sortName,
            tableSortDir,
            allFilters
        )
            .then(response => {
                if (!initialLoadComplete) {
                    setInitialLoadComplete(true);
                }
                mapDefectsToTable(response.data);
                setTotalRecords(response.totalCount);
            })
            .catch(error => {
                NotificationToaster.show(Intent.DANGER, "Could not fetch defects, please try again.");
            })
            .finally(() => {
                setLoadingData(false);
                if (!initialLoadComplete) {
                    setInitialLoadComplete(true);
                }
            });
    }

    function onSearchTextChange(item) {
        setSearchTerm(item);
        setEventListingDispatch(item);
        if (initialLoadComplete) {
            setPageNumber(1);
        }
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
        setEventListingDispatch(null, newPageNumber, newPageSize);
    }

    function onTableSort(header, direction) {
        setTableSortDir(direction);
        setTableSortBy(header);
        setEventListingDispatch(null, null, null, header, direction);
    }

    function setEventListingDispatch(newSearchTerm, newPageNumber, newPageSize, newTableSortBy, newTableSortDir) {
        dispatch(SetFiltering(defectListingReduxName, {
            searchTerm: newSearchTerm ?? searchTerm,
            pageNumber: newPageNumber ?? pageNumber,
            pageSize: newPageSize ?? pageSize,
            tableSortBy: newTableSortBy ?? tableSortBy,
            tableSortDir: newTableSortDir ?? tableSortDir
        }));
    }

    function onDateRangeChange(item) {
        const tempStartDate = item.startDate.date ? item.startDate.date.format(dateFormat) : "";
        const tempEndDate = item.endDate.date ? item.endDate.date.format(dateFormat) : "";

        setStartDate(tempStartDate);
        setEndDate(tempEndDate);
        setEventHubDispatch(tempStartDate, tempEndDate);
        if (initialLoadComplete) {
            setPageNumber(1);
        }
    }

    function onSelectedLocationsChange(locations) {
        setSelectedLocations(locations);
        setEventHubDispatch(null, null, locations);
        if (initialLoadComplete) {
            setPageNumber(1);
        }
    }

    function setEventHubDispatch(newStartDate, newEndDate, newLocations) {
        dispatch(SetFiltering(eventHubReduxName, {
            startDate: newStartDate ?? startDate,
            endDate: newEndDate ?? endDate,
            locations: newLocations ?? selectedLocations
        }));
    }

    function onFilterChange(listingFilters) {
        if (initialLoadComplete) {
            setPageNumber(1);
        }
        setAppliedFilters(listingFilters ?? []);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        })
    }

    function hideEventEditModal() {
        setIsEventEditModalOpen(false);
    }

    function onEditEvent(e) {
        e.stopPropagation();
        setIsEventEditModalOpen(true);
        if (isEventViewModalOpen === true) {
            setIsEventViewModalOpen(false);
        }
    }


    return (
        <UserAccess perform={[eventAction, checksAction, tireAction]}
            yes={() => (
                <div className={classNames({ "row": pageState !== 'HUB' })}>
                    <ShowHide
                        evaluator={pageState !== 'HUB'}
                        show={(
                            <h2>Defects</h2>
                        )}
                    />
                    <NotificationInline
                        intent="success"
                        text="Your report has been queued, and will download as soon as it's ready."
                        show={reportDownloading}
                    />
                    <div className="filter-controls inline-items spacer-bottom-small">
                        <FormDateRangeInput
                            onChange={onDateRangeChange}
                            startDate={startDate}
                            endDate={endDate}
                            disabled={loading || loadingData || savingSignoff}
                            allowClear={pageState !== 'HUB'}
                            large
                        />
                        <ShowHide
                            evaluator={pageState === 'HUB'}
                            show={(
                                <div className="inline-item fill">
                                    <div className="form-field-full">
                                        <FormTextInput inputRef={inputSearchRef} placeholder="Search fleet or defect name" onChange={(x) => onSearchTextChange(x.target.value)} value={searchTerm} large disabled={loading || loadingData || savingSignoff} icon="search" id="user-search-field" />
                                    </div>
                                </div>
                            )}
                        />
                        <ShowHide
                            evaluator={pageState === 'HUB'}
                            show={(
                                <FormLocationSelector
                                    businessArea=""
                                    loading={loading || loadingData || savingSignoff}
                                    selectedLocations={selectedLocations}
                                    setSelectedLocations={onSelectedLocationsChange}
                                    useHierarchy={false}
                                    useCustomLocations
                                    customLocations={locationList}
                                    useLocationDefaults={locationDefaultsToTrue}
                                    locationDefaultsToTrue={locationDefaultsToTrue}
                                />
                            )}
                        />
                        <ReportList
                            pageName="Defect-Listing"
                            lastSearchPayload={{
                                assetId: asset?.assetId,
                                startDate,
                                endDate,
                                appliedFilters,
                                searchTerm: pageState === "HUB" ? searchTerm : undefined,
                                selectedLocations: pageState === "HUB" ? selectedLocations : undefined
                            }}
                            onReportDownloadingChange={setReportDownloading}
                            disabled={loading || loadingData || savingSignoff}
                            compactLarge
                        />
                        <Filter
                            filterName={defectListingFilterReduxName}
                            visible={showFilters && reduxLoaded}
                            filters={pageFilters}
                            onUpdate={onFilterChange}
                            loading={loading}
                            onClickOutside={onToggleFilter}
                            numOfColumns={filterColumnCount}
                            customRenderers={{
                                ASSETTYPE: (props) => (
                                    <AssetTypeFilterRenderer
                                        key="asset-Type-filter-renderer"
                                        {...props}
                                        expandedAssetTypes={expandedAssetTypes}
                                        setExpandedAssetTypes={setExpandedAssetTypes}
                                    />
                                )
                            }}
                        />
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>
                    <ListingTable
                        headers={pageState === 'HUB' ? [...tableHeadersHub, ...tableHeadersProfile]: tableHeadersProfile}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        onPagingChange={onPagingChange}
                        loadingData={loadingData || loading}
                        noDataMessage={noDataMessage}
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={tableSortBy}
                        sortedDir={tableSortDir}
                        onSort={onTableSort}
                        pageSize={pageSize}
                        startPage={pageNumber}
                    />

                    <DefectSignoffModal
                        title="Sign Off Defect"
                        isOpen={showDefectSignoffModal}
                        loading={loading || loadingData}
                        defect={localDefect}
                        onClose={onCloseSignoffModal}
                        signOffDefect={signOffDefect}
                        saving={savingSignoff}
                        UserAccessActions={[eventEditAction, checksEditAction, tireEditAction]}
                    />

                    <ShowHide
                        evaluator={isEventViewModalOpen}
                        show={(
                            <EventViewModal
                                eventId={selectedEvent?.eventId}
                                eventTypeId={selectedEvent?.eventTypeId}
                                isOpen={isEventViewModalOpen}
                                onModalClose={onEventViewModalClose}
                                asset={selectedAsset}
                                eventOptions={eventOptions}
                                mappedAssetOptions={mappedAssetOptions}
                                loading={loading && loadingData}
                                refetchEvents={() => fetchTableData(true)}
                                eventTypesForAsset={eventTypes}
                                openEditEventModal={(e) => onEditEvent(e)}
                                requiredAction={eventAction}
                            />
                        )}
                    />

                    <ShowHide
                        evaluator={isEventEditModalOpen}
                        show={(
                            <EventEditModal
                                isOpen={isEventEditModalOpen}
                                hideModal={hideEventEditModal}
                                onSuccess={() => fetchTableData(true)}
                                eventId={selectedEvent?.eventId}
                                eventTypeId={selectedEvent?.eventTypeId}
                                asset={selectedAsset}
                                eventOptions={eventOptions}
                                mappedAssetOptions={mappedAssetOptions}
                                loading={loading && loadingData}
                            />
                        )}
                    />

                </div>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}

DefectListing.propTypes = {
    asset: PropTypes.object,
    eventOptions: PropTypes.object,
    mappedAssetOptions: PropTypes.object,
    loading: PropTypes.bool,
    pageState: PropTypes.oneOf(['PROFILE', 'HUB']).isRequired,
    locationList: PropTypes.object,
    eventTypes: PropTypes.array
};